import { useEffect, useRef, useState } from "react";
import { captureToolEmail, TOOL_SLUGS, warmCaptureFunction } from "@/lib/tools/tool-capture";
import { trackMetaEvent } from "@/lib/meta-pixel";
import { suggestEmail } from "@/lib/email-typo";
import { useTurnstile } from "@/lib/turnstile";
import { STORM_OFFER } from "./storm-landing";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The sign-up box, in the order a reader decides: what they get, the field,
 * the button, then the consent. With no guide, the proof line and what removes
 * the risk follow it.
 * Everything in it is the same on every version except `outcome`, the
 * version's dream outcome. `landingVariant` says which page the sign-up came
 * from.
 */
export function StormOfferCard({
  landingVariant,
  outcome = "",
  review = false,
}: {
  landingVariant: string;
  outcome?: string;
  /** The review link: the sign-up still works, but is never sent to Meta. */
  review?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [trap, setTrap] = useState("");
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submittingRef = useRef(false);
  const turnstile = useTurnstile();

  useEffect(() => {
    warmCaptureFunction();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (submittingRef.current) return;

    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      await captureToolEmail(trimmed, TOOL_SLUGS.stormLanding, {
        landing_variant: landingVariant,
        website: trap,
        turnstile_token: await turnstile.getToken(),
      });
      // Only after the lead is saved: a Lead the ad platform counts but our
      // table never got would make the ads look better than they are.
      if (!review) trackMetaEvent("Lead");
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      turnstile.reset();
      setLoading(false);
      submittingRef.current = false;
    }
  }

  return (
    <section className="mt-5 rounded-xl border border-primary/30 bg-primary/10 p-4 sm:mt-6 sm:p-6">
      {STORM_OFFER.download ? (
        <GuideHeading outcome={outcome} />
      ) : (
        <SubscriptionHeading outcome={outcome} />
      )}

      {done ? (
        <div role="status" className="mt-5 text-center">
          <p className="text-lg font-semibold">{STORM_OFFER.thanksTitle}</p>
          {STORM_OFFER.download && (
            <a
              href={STORM_OFFER.download.path}
              download
              className="mt-4 block w-full rounded-lg bg-primary px-6 py-3 text-center text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {STORM_OFFER.download.label}
            </a>
          )}
          <p className="mt-3 text-base text-foreground">{STORM_OFFER.thanksBody}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="relative mt-4 space-y-3 sm:mt-5" noValidate>
          {/* The bot trap: off-screen rather than display:none, which some
              bots skip, and out of reach of the keyboard, autofill and screen
              readers, so no person ever fills it. */}
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={trap}
              onChange={(e) => setTrap(e.target.value)}
            />
          </div>
          <label htmlFor="storm-email" className="sr-only">
            Email address
          </label>
          <input
            id="storm-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
              setHint(null);
            }}
            onBlur={() => setHint(suggestEmail(email.trim()))}
            placeholder="you@example.com"
            className="w-full rounded-lg border-2 border-foreground/40 bg-background px-4 py-3 text-lg text-foreground shadow-sm placeholder:text-foreground/75 focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/40"
          />
          {hint && (
            <button
              type="button"
              onClick={() => {
                setEmail(hint);
                setHint(null);
              }}
              className="block w-full cursor-pointer text-center text-base text-foreground underline underline-offset-2"
            >
              Did you mean {hint}?
            </button>
          )}
          {error && <p className="text-center text-base text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full cursor-pointer rounded-lg bg-primary px-6 py-3 text-lg font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? STORM_OFFER.busy : STORM_OFFER.button}
          </button>
          {/* The consent sits right at the button, before anything else. */}
          <p className="text-center text-base text-foreground">{STORM_OFFER.finePrint}</p>
          {!STORM_OFFER.download && (
            <div className="text-center text-base">
              <p className="text-foreground">{STORM_OFFER.proof}</p>
              <p className="mt-1 font-medium">{STORM_OFFER.assurance}</p>
            </div>
          )}
          <div ref={turnstile.ref} />
          {STORM_OFFER.delivery && (
            <p className="text-center text-base text-foreground">{STORM_OFFER.delivery}</p>
          )}
        </form>
      )}
      {STORM_OFFER.body && (
        <p className="mt-4 text-base text-foreground">{STORM_OFFER.body}</p>
      )}
    </section>
  );
}

/**
 * The guide leads, and the free month is thrown in on top. The cover has to be
 * big enough to read its title on a phone; the line on the free month takes the
 * full width below it, so the cover does not squeeze the text.
 */
function GuideHeading({ outcome }: { outcome: string }) {
  return (
    <>
      <div className="flex items-center gap-3">
        {STORM_OFFER.cover && (
          <img
            src={STORM_OFFER.cover}
            alt={`Cover of ${STORM_OFFER.title}`}
            className="w-32 shrink-0 shadow-md sm:w-40"
          />
        )}
        <div>
          <h2 className="font-heading text-xl font-bold leading-tight sm:text-2xl">
            {STORM_OFFER.eyebrow}: {STORM_OFFER.title}
          </h2>
          {outcome && <p className="mt-2 text-lg font-semibold leading-snug">{outcome}</p>}
        </div>
      </div>
      {STORM_OFFER.subtitle && <p className="mt-3 text-center text-base leading-snug">{STORM_OFFER.subtitle}</p>}
    </>
  );
}

/** No guide configured: the free month is the offer, so its points stay in the box. */
function SubscriptionHeading({ outcome }: { outcome: string }) {
  return (
    <div>
      {outcome ? (
        <>
          <h2 className="font-heading text-xl font-bold sm:text-2xl">{outcome}</h2>
          <p className="mt-1 text-base font-semibold sm:text-base">{STORM_OFFER.withOutcome}</p>
        </>
      ) : (
        <>
          <p className="text-base font-semibold uppercase tracking-wider text-primary">
            {STORM_OFFER.eyebrow}
          </p>
          <h2 className="mt-1 font-heading text-xl font-bold sm:mt-2 sm:text-2xl">
            {STORM_OFFER.title}
          </h2>
          {STORM_OFFER.subtitle && (
            <p className="mt-1 text-base font-semibold sm:text-base">{STORM_OFFER.subtitle}</p>
          )}
        </>
      )}
      <OfferPoints />
    </div>
  );
}

function OfferPoints() {
  return (
    <ul className="mt-2 space-y-0.5 text-base">
      {STORM_OFFER.points.map((point) => (
        <li key={point} className="flex gap-2">
          <span aria-hidden="true" className="font-bold text-primary">
            ✓
          </span>
          <span>{point}</span>
        </li>
      ))}
    </ul>
  );
}
